import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { languages, Language } from '../i18n';
import { useAuthStore } from '../stores/authStore';
import { useNbTheme } from '../hooks/useNbTheme';
import { useClickOutside } from '../hooks/useClickOutside';
import ButtonNB from './ui-nb/ButtonNB';
import SelectNB from './ui-nb/SelectNB';
import ThemeToggleNB from './ThemeToggleNB';
import LogoMark from './LogoMark';

export default function NavBarNB() {
  const { t, language, setLanguage } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const { isAuthenticated, user, signOut } = useAuthStore();
  const { colors, theme } = useNbTheme();

  const handleLanguageChange = (newLang: Language) => {
    setLanguage(newLang);
    navigate(`/${newLang}`);
  };

  useClickOutside(menuRef, () => setMenuOpen(false), menuOpen);

  const handleSignOut = () => {
    signOut();
    setMenuOpen(false);
    navigate(`/${language}`);
  };

  return (
    <header
      ref={menuRef}
      className="sticky top-0 z-50 border-b-3 relative"
      style={{ background: colors.bgCard, borderColor: theme === 'light' ? '#000000' : '#FFFFFF' }}
    >
      <nav className="max-w-6xl mx-auto px-4 py-3 grid grid-cols-[auto_1fr_auto] items-center">
        {/* Brand name */}
        <a href="/en" className="flex items-center gap-2 font-nb font-bold text-xl" style={{ color: colors.textPrimary }}>
          <LogoMark size={28} />
          Tellow Up
        </a>

        {/* Desktop Nav links — perfectly centered */}
        <div className="hidden lg:flex items-center gap-6 justify-center">
          <a href="/en/support" className="text-sm hover:underline hover:decoration-nb-yellow hover:decoration-2 font-medium transition-colors whitespace-nowrap" style={{ color: colors.textPrimary }}>
            {t('nav.support')}
          </a>
          <a href="/en/contact" className="text-sm hover:underline hover:decoration-nb-yellow hover:decoration-2 font-medium transition-colors whitespace-nowrap" style={{ color: colors.textPrimary }}>
            {t('nav.contact')}
          </a>
          <a href="/en/track" className="text-sm hover:underline hover:decoration-nb-yellow hover:decoration-2 font-medium transition-colors whitespace-nowrap" style={{ color: colors.textPrimary }}>
            {t('nav.trackRepair')}
          </a>
        </div>

        {/* Desktop Auth CTAs + Language selector + Theme toggle */}
        <div className="hidden lg:flex items-center gap-3 justify-self-end">
          <ThemeToggleNB />
          <SelectNB
            value={language}
            onChange={(e) => handleLanguageChange(e.target.value as Language)}
            selectSize="sm"
          >
            {languages.map((lang) => (
              <option key={lang.code} value={lang.code}>{lang.name}</option>
            ))}
          </SelectNB>

          {isAuthenticated ? (
            <div className="flex items-center gap-2">
              <span className="text-sm" style={{ color: colors.textPrimary }}>{user?.name}</span>
              <ButtonNB variant="secondary" size="sm" onClick={handleSignOut}>
                {t('nav.logout')}
              </ButtonNB>
            </div>
          ) : (
            <>
              <a href="/en/login">
                <ButtonNB variant="secondary" size="sm">
                  {t('nav.login')}
                </ButtonNB>
              </a>
              <a href="/en/register">
                <ButtonNB variant="primary" size="sm">
                  {t('nav.register')}
                </ButtonNB>
              </a>
            </>
          )}
        </div>

        {/* Mobile menu button — pinned to the rightmost grid column */}
        <button
          className="lg:hidden col-start-3 justify-self-end p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
          style={{ color: colors.textPrimary }}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
        >
          {menuOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </nav>

      {/* Mobile menu — floats over content */}
      {menuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 border-t-3 px-4 py-4 shadow-lg" style={{ borderColor: colors.textPrimary, background: colors.bgCard }}>
          <div className="flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
            {/* Navigation links */}
            <a href="/en/support" className="text-sm font-medium px-3 py-2 rounded-[2px] border-3 border-transparent transition-all duration-150 hover:border-4" style={{ color: colors.textPrimary, borderColor: 'transparent' }}
               onClick={() => setMenuOpen(false)}>
              {t('nav.support')}
            </a>
            <a href="/en/contact" className="text-sm font-medium px-3 py-2 rounded-[2px] border-3 border-transparent transition-all duration-150 hover:border-4" style={{ color: colors.textPrimary, borderColor: 'transparent' }}
               onClick={() => setMenuOpen(false)}>
              {t('nav.contact')}
            </a>
            <a href="/en/track" className="text-sm font-medium px-3 py-2 rounded-[2px] border-3 border-transparent transition-all duration-150 hover:border-4" style={{ color: colors.textPrimary, borderColor: 'transparent' }}
               onClick={() => setMenuOpen(false)}>
              {t('nav.trackRepair')}
            </a>

            <hr style={{ borderColor: colors.textPrimary, opacity: 0.2 }} />

            {/* Theme toggle */}
            <ThemeToggleNB />

            {/* Language selector */}
            <SelectNB
              value={language}
              onChange={(e) => handleLanguageChange(e.target.value as Language)}
              selectSize="sm"
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>{lang.name}</option>
              ))}
            </SelectNB>

            {/* Auth buttons */}
            {isAuthenticated ? (
              <ButtonNB variant="secondary" size="md" onClick={handleSignOut} className="w-full">
                {t('nav.logout')}
              </ButtonNB>
            ) : (
              <div className="flex gap-2">
                <a href="/en/login" className="flex-1">
                  <ButtonNB variant="secondary" size="md" className="w-full">
                    {t('nav.login')}
                  </ButtonNB>
                </a>
                <a href="/en/register" className="flex-1">
                  <ButtonNB variant="primary" size="md" className="w-full">
                    {t('nav.register')}
                  </ButtonNB>
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
